import { useTranslation } from 'react-i18next'
import { FormikContextType, useFormikContext } from 'formik'
import {
  Skeleton,
  IconInfoInverted,
  ButtonIcon,
  RadioBarItem,
  IconPropertyApartment,
  IconPropertyHouse,
  IconPropertyTownhouse,
  IconSpacePrivate,
  IconSpaceRoom,
  IconSpaceShared,
  useDialogControllers
} from '@machobear/design-system'
import useProfile from '@/hooks/use-profile'
import { FieldRadioBar } from '@/components/ds-form'
import PropertyTypeModal from './property-type-modal'
import RentalSpaceModal from './rental-space-modal'
import ListingTypeModal from './listing-type-modal'
import { FormBox, FormSection } from '../../components'
import { CreateFormValues } from '../../types'

interface Props {
  isLoading: boolean
  isApartment: boolean
}

const PropertyTypeSection = ({ isLoading, isApartment }: Props) => {
  const { t } = useTranslation()
  const { values, setValues, setFieldValue }: FormikContextType<CreateFormValues> =
    useFormikContext<CreateFormValues>()
  const profile = useProfile()
  const isEnterprise = profile?.company_info?.registration_plan === 'enterprise'

  const { visible, showModal, closeModal } = useDialogControllers({
    propertyType: false,
    rentalSpace: false,
    listingType: false
  })

  const clearBuildingValues = async () => {
    await setValues({
      ...values,
      full_address: { text: '', value: '' },
      building: '',
      address: '',
      suite: '',
      street_number: '',
      building_id: '',
      building_name: '',
      city: '',
      province: '',
      zip: ''
    })
  }

  return (
    <>
      <PropertyTypeModal visible={visible.propertyType} closeModal={closeModal('propertyType')} />
      <RentalSpaceModal visible={visible.rentalSpace} closeModal={closeModal('rentalSpace')} />
      <ListingTypeModal visible={visible.listingType} closeModal={closeModal('listingType')} />
      <FormBox label={t('Property Details', 'Property Details')}>
        {isLoading ? (
          <Skeleton.Shape height="58px" />
        ) : (
          <>
            <FormSection
              id="type"
              label={t('Property type', 'Property type')}
              required
              tooltip={
                <ButtonIcon
                  id="property-type-info"
                  label={t('Property type info', 'Property type info')}
                  variant="transparent"
                  icon={<IconInfoInverted />}
                  onClick={showModal('propertyType')}
                  tone="secondary"
                />
              }
            >
              <FieldRadioBar
                label=""
                required
                aria-labelledby="type"
                name="type"
                onChange={(value: string) => {
                  clearBuildingValues()
                  setFieldValue('groupListing', '0')
                  setFieldValue('type', value)
                }}
              >
                <RadioBarItem value="condo" icon={<IconPropertyApartment />}>
                  {t('common.apartment', 'Apartment')}
                </RadioBarItem>
                <RadioBarItem value="house" icon={<IconPropertyHouse />}>
                  {t('common.house', 'House')}
                </RadioBarItem>
                <RadioBarItem value="townhouse" icon={<IconPropertyTownhouse />}>
                  {t('common.townhouse', 'Townhouse')}
                </RadioBarItem>
              </FieldRadioBar>
            </FormSection>
            <FormSection
              id="scope"
              label={t('Rental space', 'Rental space')}
              required
              tooltip={
                <ButtonIcon
                  id="rental-space-info"
                  label={t('Rental space info', 'Rental space info')}
                  variant="transparent"
                  icon={<IconInfoInverted />}
                  onClick={showModal('rentalSpace')}
                  tone="secondary"
                />
              }
            >
              <FieldRadioBar
                label=""
                required
                aria-labelledby="scope"
                name="scope"
                onChange={(value: string) => {
                  clearBuildingValues()
                  setFieldValue('groupListing', '0')
                  setFieldValue('scope', value)
                }}
              >
                <RadioBarItem value="entire" icon={<IconSpacePrivate />}>
                  {t('common.private', 'Private')}
                </RadioBarItem>
                <RadioBarItem value="partial" icon={<IconSpaceShared />}>
                  {t('common.shared', 'Shared')}
                </RadioBarItem>
                <RadioBarItem value="single" icon={<IconSpaceRoom />}>
                  {t('common.room', 'Room')}
                </RadioBarItem>
              </FieldRadioBar>
            </FormSection>
            {isApartment && isEnterprise && (
              <FormSection
                id="groupListing"
                label={t('Listing type', 'Listing type')}
                required
                tooltip={
                  <ButtonIcon
                    id="listing-type-info"
                    label={t('Listing type info', 'Listing type info')}
                    variant="transparent"
                    icon={<IconInfoInverted />}
                    onClick={showModal('listingType')}
                    tone="secondary"
                  />
                }
              >
                <FieldRadioBar
                  label=""
                  required
                  aria-labelledby="groupListing"
                  name="groupListing"
                  onChange={(value: string) => setFieldValue('groupListing', value)}
                >
                  <RadioBarItem value="0" icon={<IconPropertyApartment />}>
                    {t('l.listing.create.single_listing.label', 'Single listing')}
                  </RadioBarItem>
                  <RadioBarItem value="1" icon={<IconPropertyHouse />}>
                    {t('l.listing.create.group_listing.label', 'Multi-unit listing')}
                  </RadioBarItem>
                </FieldRadioBar>
              </FormSection>
            )}
          </>
        )}
      </FormBox>
    </>
  )
}

export default PropertyTypeSection
